import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import { useUserId } from "@/hooks/useUserId";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LogOut, Sparkles, AlertCircle } from "lucide-react";
import { processWebhooks } from "@/utils/webhook";
import { daysUntilDataDeletion } from "@/lib/subscription";
import { InspirationSource } from "@/types/webhook";
import { extractInstagramUsername, isValidInstagramUsername, isRealInstagramUsername } from "@/utils/instagramUsername";
import OnboardingPricingStep from "@/components/onboarding/OnboardingPricingStep";
import { supabase } from "@/integrations/supabase/client";
import { logEdgeFunctionError, logError } from "@/utils/sentry";
import { parseStoredCategory } from "@/constants/categoryTaxonomy";

type PageState = "pricing" | "processing" | "success";

const Subscribe = () => {
  const [pageState, setPageState] = useState<PageState>("pricing");
  const [preselectedPlan, setPreselectedPlan] = useState<{ planName: string; billing: "monthly" | "yearly" } | null>(null);
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [checkoutReturned, setCheckoutReturned] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signOut, loading: authLoading } = useAuth();
  const {
    subscribed,
    loading: subLoading,
    refresh: refreshSubscription,
    subscriptionCancelledAt,
    eligibleForTrial,
  } = useSubscription();
  const userId = useUserId();
  const { saveInspirationSources } = useOnboarding();
  const { toast } = useToast();

  // Redirect already-subscribed users to panel
  useEffect(() => {
    if (!subLoading && subscribed && searchParams.get("checkout") !== "success") {
      navigate("/panel", { replace: true });
    }
  }, [subLoading, subscribed, searchParams, navigate]);

  // Hard gate: never let a user reach payment without a real Instagram
  // username on `user_profiles`. Settings can only ADD a username when none
  // is set (see InstagramProfileSettings.tsx) — it cannot change one. So a
  // paying user with an empty/sentinel username is permanently stuck.
  // Skip the check on the post-checkout return — at that point the payment
  // already happened and the post-payment handler will reconcile the row.
  useEffect(() => {
    if (searchParams.get("checkout") === "success") return;
    if (authLoading || !userId) return;

    let cancelled = false;
    const guard = async () => {
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("instagram_username")
        .eq("user_id", userId)
        .maybeSingle();

      if (cancelled) return;

      if (!isRealInstagramUsername(profile?.instagram_username)) {
        toast({
          title: "Brakuje nazwy Instagram",
          description:
            "Dodaj swoją nazwę użytkownika Instagram, zanim przejdziesz do płatności.",
          variant: "destructive",
        });
        navigate("/onboarding/instagram", { replace: true });
      }
    };
    guard();
    return () => {
      cancelled = true;
    };
  }, [authLoading, userId, searchParams, navigate, toast]);

  // Detect return from Stripe checkout — mark it, but wait for userId
  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      setCheckoutReturned(true);
      setPageState("processing");
    }
  }, [searchParams]);

  // Run post-payment processing only when userId is available
  useEffect(() => {
    if (checkoutReturned && !authLoading && userId) {
      handlePostPayment();
      setCheckoutReturned(false);
    }
  }, [checkoutReturned, authLoading, userId]);

  // Check for pre-selected plan from landing page
  useEffect(() => {
    const savedPlan = localStorage.getItem("selected_plan");
    if (savedPlan) {
      try {
        const parsed = JSON.parse(savedPlan);
        localStorage.removeItem("selected_plan");
        setPreselectedPlan(parsed);
      } catch {
        // ignore
      }
    }
  }, []);

  // Animate progress during processing
  useEffect(() => {
    if (pageState === "processing") {
      setAnimatedProgress(0);
      const TOTAL_DURATION_MS = 45000;
      const MAX_PROGRESS = 95;
      const INTERVAL_MS = 200;
      const increment = (MAX_PROGRESS / TOTAL_DURATION_MS) * INTERVAL_MS;
      const interval = setInterval(() => {
        setAnimatedProgress((prev) => {
          const next = prev + increment;
          return next >= MAX_PROGRESS ? MAX_PROGRESS : next;
        });
      }, INTERVAL_MS);
      return () => clearInterval(interval);
    }
  }, [pageState]);

  const handlePostPayment = async () => {
    const startTime = Date.now();
    const MIN_PROCESSING_MS = 45000; // 45s for n8n profile scraping

    try {
      await refreshSubscription();

      // Idempotent re-entry on hard reload of /subscribe?checkout=success:
      // the cleanup at the end of this handler removes `onboarding_instagram`,
      // so a second run sees `isFromOnboarding=false` and short-circuits.
      //
      // We deliberately do NOT short-circuit on the broader
      // `onboarding_completed` flag here. That flag persists across users
      // on the same device — so when account A finishes onboarding on a
      // shared browser and account B later signs up, B's payment-success
      // would skip processing entirely and B's inspirations + competitors
      // would never land in the DB. The per-onboarding-attempt
      // `onboarding_instagram` signal is the right granularity.
      const isFromOnboarding = !!localStorage.getItem("onboarding_instagram");

      if (!isFromOnboarding) {
        setPageState("success");
        return;
      }

      // 1. Reconcile user profile.
      //
      // Earlier onboarding steps already persisted what they own:
      //   • InstagramSetup wrote instagram_username + onboarding_started
      //   • IndustrySelection wrote category via useOnboarding.saveUserProfile
      //
      // This post-payment job must NEVER overwrite a real value with "".
      // Read the current row first and only fill fields that are genuinely
      // missing, falling back to localStorage for anything the row lacks.
      const lsInstagram = localStorage.getItem("onboarding_instagram") || "";
      if (lsInstagram) {
        const { data: existingProfile } = await supabase
          .from("user_profiles")
          .select("instagram_username, category")
          .eq("user_id", userId)
          .maybeSingle();

        const storedSubcategories = localStorage.getItem("onboarding_subcategories");
        const { subcategoryIds, legacy: storedIsLegacy } = parseStoredCategory(storedSubcategories);
        const lsCategory =
          storedSubcategories && !storedIsLegacy && subcategoryIds.length
            ? storedSubcategories
            : "";

        const dbInstagram = existingProfile?.instagram_username || "";
        const dbCategory = existingProfile?.category || "";
        const dbHasRealInstagram = isRealInstagramUsername(dbInstagram);

        const profilePatch: {
          user_id: string;
          updated_at: string;
          onboarding_started: boolean;
          instagram_username: string;
          category?: string;
        } = {
          user_id: userId,
          updated_at: new Date().toISOString(),
          onboarding_started: true,
          // We only enter this branch when `lsInstagram` is truthy (see the
          // outer guard), so this expression always resolves to a real,
          // verified username. We never re-introduce the legacy "pending_setup"
          // sentinel here — handleConfirmContinue + this branch are the only
          // writers of `instagram_username` and both produce a real value.
          instagram_username: dbHasRealInstagram ? dbInstagram : lsInstagram,
        };

        // Only write category when we have a real value. Never blank it.
        if (!dbCategory && lsCategory) {
          profilePatch.category = lsCategory;
        }

        const { error: profileError } = await supabase
          .from("user_profiles")
          .upsert(profilePatch, { onConflict: "user_id" });
        if (profileError) {
          logError("Subscribe: post-payment profile reconcile", profileError, {
            step: "subscribe-post-payment-profile",
          });
        }

        const effectiveInstagram = dbHasRealInstagram ? dbInstagram : lsInstagram;
        if (isRealInstagramUsername(effectiveInstagram)) {
          try {
            await supabase.functions.invoke("trigger-profile-scraping", {
              body: { username: effectiveInstagram },
            });
          } catch (err) {
            logEdgeFunctionError("trigger-profile-scraping", err, {
              username: effectiveInstagram,
            });
          }
        }
      }

      // 2. Process inspiration sources webhooks (on-demand ideas scraper)
      const savedInspirationSources = localStorage.getItem("onboarding_inspiration_sources");
      if (savedInspirationSources) {
        try {
          const parsed = JSON.parse(savedInspirationSources);
          const inspirationSources: InspirationSource[] = parsed
            .filter((c: string) => c.trim() !== "")
            .map((input: string) => {
              const username = extractInstagramUsername(input);
              return username && isValidInstagramUsername(username)
                ? { username, platform: "instagram" as const }
                : null;
            })
            .filter(Boolean) as InspirationSource[];

          if (inspirationSources.length > 0) {
            const saveResult = await saveInspirationSources(userId, inspirationSources);
            if (!saveResult.success) {
              // Don't abort — still kick off scraping for whatever did
              // land — but make sure the failure is visible in Sentry
              // instead of disappearing into a console.warn from the hook.
              logError("Subscribe: post-payment inspiration save", saveResult.error, {
                step: "subscribe-post-payment-inspirations",
                requestedCount: inspirationSources.length,
                savedCount: saveResult.savedCount ?? 0,
              });
            }
            await processWebhooks(userId, inspirationSources, () => {});
          }
        } catch (err) {
          logError("Subscribe: post-payment inspiration sources failed", err, {
            step: "subscribe-post-payment-inspirations-exception",
          });
        }
      }

      // 3. Save competitors. Insert one-by-one so that a duplicate or
      // tier-limit failure on one row doesn't abort the rest of the batch
      // (matters when this handler retries after a reload).
      const savedCompetitors = localStorage.getItem("onboarding_add_competitors");
      if (savedCompetitors) {
        try {
          const parsed = JSON.parse(savedCompetitors);
          const validCompetitors = (parsed as string[]).filter((c) => c.trim() !== "");
          for (const input of validCompetitors) {
            const username = extractInstagramUsername(input);
            if (!username) continue;
            const { error: compError } = await supabase.from("competitors").insert({
              user_id: userId,
              instagram_username: username,
              is_active: true,
            });
            if (compError && compError.code !== "23505") {
              // Stop on non-duplicate errors (e.g. tier limit hit). Surface
              // to Sentry so silent post-payment data-loss is visible.
              logError("Subscribe: post-payment competitor insert stopped", compError, {
                step: "subscribe-post-payment-competitors",
                username,
              });
              break;
            }
          }
        } catch (err) {
          logError("Subscribe: post-payment competitors processing failed", err, {
            step: "subscribe-post-payment-competitors-exception",
          });
        }
      }

      // 4. Clean up onboarding localStorage
      localStorage.removeItem("onboarding_instagram");
      localStorage.removeItem("onboarding_industry");
      localStorage.removeItem("onboarding_inspiration_sources");
      localStorage.removeItem("onboarding_add_competitors");
      localStorage.removeItem("onboarding_subcategories");
      localStorage.removeItem("onboarding_inspiration_verifications");
      localStorage.removeItem("onboarding_competitor_verifications");
      localStorage.removeItem("onboarding_inspiration_confirmed");
      localStorage.removeItem("onboarding_competitors_confirmed");
      sessionStorage.removeItem("onboarding_payment_guard_passed");
      localStorage.removeItem("onboarding_main_category"); // legacy key
      localStorage.removeItem("onboarding_subcategory"); // legacy key
      localStorage.removeItem("onboarding_from_instagram");
      localStorage.setItem("onboarding_completed", "true");

      // Wait remaining time so n8n scraping can finish (~1 min total)
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_PROCESSING_MS) {
        await new Promise((r) => setTimeout(r, MIN_PROCESSING_MS - elapsed));
      }

      setPageState("success");
    } catch {
      // Still wait minimum time on error so profile scraping can complete
      const elapsed = Date.now() - startTime;
      if (elapsed < MIN_PROCESSING_MS) {
        await new Promise((r) => setTimeout(r, MIN_PROCESSING_MS - elapsed));
      }
      setPageState("success");
    }
  };

  if (pageState === "success") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
        <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16 flex items-center justify-center min-h-screen">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="max-w-md w-full mx-auto backdrop-blur-xl bg-white/40 rounded-3xl shadow-2xl border border-white/50 p-10 text-center space-y-8"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="w-14 h-14 mx-auto rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 flex items-center justify-center"
            >
              <Sparkles className="h-7 w-7 text-primary" />
            </motion.div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Wszystko gotowe</h2>
              <p className="text-muted-foreground text-sm">
                Twoje konto jest aktywne. Czas tworzyć viralowe treści.
              </p>
            </div>
            <Button
              size="lg"
              className="w-full h-12 text-base font-semibold rounded-xl"
              onClick={async () => {
                await refreshSubscription();
                navigate("/panel", { replace: true });
              }}
            >
              Przejdź do panelu
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  if (pageState === "processing") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
        <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16 flex items-center justify-center min-h-screen">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="max-w-md w-full mx-auto backdrop-blur-xl bg-white/40 rounded-3xl shadow-2xl border border-white/50 p-10"
          >
            <div className="text-center space-y-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                className="w-14 h-14 mx-auto rounded-2xl bg-white/60 backdrop-blur-sm border border-white/50 flex items-center justify-center"
              >
                <Sparkles className="h-7 w-7 text-primary" />
              </motion.div>
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-2">Przygotowujemy Twoje konto</h2>
                <p className="text-sm text-muted-foreground">Analizujemy Twój profil i zbieramy dane...</p>
              </div>
              <Progress value={animatedProgress} className="h-1.5" />
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Pricing state
  const reason = searchParams.get("reason");
  const daysLeft =
    reason === "cancelled"
      ? daysUntilDataDeletion(subscriptionCancelledAt)
      : null;
  const showCancelledBanner = reason === "cancelled" && daysLeft !== null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />

      <div className="relative z-20 flex justify-end px-4 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={signOut}
          className="text-muted-foreground hover:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Wyloguj się
        </Button>
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-4 md:py-12">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-primary tracking-tight">Viraluj.pl</h1>
          <p className="text-muted-foreground mt-1">Wybierz plan, aby kontynuować</p>
        </div>
        {showCancelledBanner && (
          <div
            role="alert"
            className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50/95 backdrop-blur-sm px-4 py-3 text-sm text-amber-900 shadow-sm"
          >
            <AlertCircle className="w-5 h-5 mt-0.5 text-amber-600 flex-shrink-0" />
            <p>
              Twoja subskrypcja została anulowana. Twoje dane czekają jeszcze{" "}
              <strong>
                {daysLeft} {daysLeft === 1 ? "dzień" : "dni"}
              </strong>{" "}
              — wznów subskrypcję, aby je zachować.
            </p>
          </div>
        )}
        <OnboardingPricingStep
          initialPlan={preselectedPlan}
          onBack={() => {
            // Deterministic back-target. navigate(-1) was unpredictable
            // because /subscribe is reached via several paths (Auth
            // redirect with replace:true, ProtectedRoute lockout redirect,
            // direct deep-link) and history depth differs in each case.
            // Returning subscribers (cancelled plan) go to /panel so they
            // can review their account; mid-onboarding users return to
            // the previous form step for a clean step-by-step rewind.
            if (subscriptionCancelledAt) {
              navigate("/panel");
            } else {
              navigate("/onboarding/add-competitors");
            }
          }}
          eligibleForTrial={eligibleForTrial && !subscriptionCancelledAt}
        />
      </div>
    </div>
  );
};

export default Subscribe;
